import { ReactNode } from 'react';
import './DataTable.css';

interface Column<T> {
    header: string;
    accessor: keyof T | ((item: T) => ReactNode);
    className?: string;
}

interface DataTableProps<T> {
    data: T[];
    columns: Column<T>[];
    keyField: keyof T;
    isLoading?: boolean;
    onRowClick?: (item: T) => void;
    emptyMessage?: string;
}

export default function DataTable<T>({
    data,
    columns,
    keyField,
    isLoading = false,
    onRowClick,
    emptyMessage = 'No data available',
}: DataTableProps<T>) {
    if (isLoading) {
        return (
            <div className="data-table-loading">
                <div className="spinner">⟳</div>
                <p>Loading data...</p>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="data-table-empty">
                <p>{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className="data-table-container glass">
            <table className="data-table">
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th key={index} className={col.className}>
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((item) => (
                        <tr
                            key={String(item[keyField])}
                            onClick={() => onRowClick && onRowClick(item)}
                            className={onRowClick ? 'clickable' : ''}
                        >
                            {columns.map((col, index) => (
                                <td key={index} className={col.className}>
                                    {typeof col.accessor === 'function'
                                        ? col.accessor(item)
                                        : (item[col.accessor] as ReactNode)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
